import {
  adminUpdatePharmacistCapacity,
  capacityIdKey,
  IAdminPharmacistCapacityList,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import {
  Constants,
  DynamicTableCell,
  TextFieldVariants,
} from '../../../helpers/Constants';
import { IHandlePublishSignature } from '../../../hooks/Admin/usePublishConfigurations';
import { IInputModalConfig } from '../../Modals/InputModal/InputModal';
import editIcon from '../../../assets/svg/updateNoteIcon.svg';
import genericClasses from '../../../theme/GenericClasses';
import deleteIcon from '../../../assets/svg/delete.svg';
import strings from '../../../localization';

import styles from './styles';

export const pharmacistCapacityHeaders = () =>
  [
    {
      key: '0',
      label: strings.from,
      sortingKey: 'from',
    },
    {
      key: '1',
      label: strings.to,
      sortingKey: 'to',
    },
    {
      key: '2',
      label: strings.published,
      sortingKey: 'published',
    },
    {
      key: '3',
      label: strings.action,
      isCenterAligned: true,
    },
  ];

export const pharmacistCapacityMap = (
  capacityList: IAdminPharmacistCapacityList[],
  publishedRecords: Record<string, boolean>,
  handlePublish: IHandlePublishSignature,
  updateAction: (capacityId: string) => void,
  showDeleteConfirmation: (capacityId: string) => void,
  canChangeParameters: boolean,
  canDelete: boolean,
) =>
  capacityList?.map((item) => {
    const { from, to, inUse } = item ?? {};
    const id = item[idStore.capacityId as keyof typeof item] as string;

    const value = [
      canChangeParameters && {
        key: '1',
        icon: editIcon,
        show: false,
        tooltip: strings.edit,
        onClick: () =>
          updateAction?.(id),
      },
      canDelete && {
        key: '2',
        icon: deleteIcon,
        show: false,
        tooltip: strings.delete,
        onClick: () =>
          showDeleteConfirmation(id),
      },
    ];

    return {
      key: id,
      data: [
        {
          key: `0-${id}`,
          value: from,
          type: DynamicTableCell.text,
        },
        {
          key: `1-${id}`,
          value: to,
          type: DynamicTableCell.text,
        },
        {
          key: `2-${id}`,
          value: {
            checked: !!publishedRecords[id],
            tooltipText: strings.inUse,
            disabled: inUse || !canChangeParameters,
            handlePress: handlePublish?.(
              {
                [idStore.capacityId as capacityIdKey]: id,
                from,
                to,
                published: !publishedRecords[id],
              },
              idStore.capacityId as capacityIdKey,
              adminUpdatePharmacistCapacity,
            ),
          },
          type: DynamicTableCell.checkbox,
          isCenterAligned: true,
        },
        !inUse
          ? {
            key: `3-${id}`,
            isCenterAligned: true,
            value,
            type: DynamicTableCell.bar,
          }
          : {
            key: `4-${id}`,
            value: {
              label: strings.inUse,
              style: styles.chipStyle,
            },
            type: DynamicTableCell.chip,
            isCenterAligned: true,
          },
      ],
    };
  });

export const addNewPharmacistCapacityForm = (): IInputModalConfig[] =>
  [
    {
      key: '0',
      name: 'from',
      type: Constants.formInputType.text,
      gridLabel: strings.from,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      fieldXs: 8,
      labelXs: 4,
    },
    {
      key: '1',
      name: 'to',
      type: Constants.formInputType.text,
      gridLabel: strings.to,
      customTextFieldStyle: genericClasses.customFilledInput,
      variant: TextFieldVariants.filled,
      fieldXs: 8,
      labelXs: 4,
    },
    {
      key: '4',
      name: 'published',
      type: Constants.formInputType.checkbox,
      customClass: genericClasses.checkboxContainer,
      customCheckboxStyle: genericClasses.checkboxStyle,
      gridLabelClass: styles.gridLabel,
      gridLabel: strings.publish,
      label: ' ',
      fieldXs: 8,
      labelXs: 4,
    },
  ];

export const canCreateNew = (
  canChangeParameters: boolean,
  object: { Component: React.ReactNode; key: string; onClick?: () => void },
) => {
  if (canChangeParameters) {
    return [object];
  }
  return [];
};
